/** Semantic status for a value tracked against a limit/threshold. */
export type LimitStatus = 'within-limit' | 'near-limit' | 'exceeded';

export interface LimitThresholds {
  /** Percent (0-100) below which the value counts as exceeded (below minimum). */
  min?: number;
  /** Percent (0-100) above which the value counts as exceeded (over limit). */
  max?: number;
  /**
   * Distance, in percentage points, from `min`/`max` within which the value
   * is flagged `near-limit` instead of `within-limit`. Defaults to 10.
   */
  warningBuffer?: number;
}

/**
 * Derives a 3-state limit status from a percentage value and its
 * min/max thresholds — used by risk limits, allocation drift, and mandate
 * compliance bars. A value past `max` or below `min` is `exceeded`; a value
 * within `warningBuffer` points of either bound is `near-limit`; otherwise
 * it's `within-limit`. Thresholds are optional independently, so a bar can
 * track a ceiling only, a floor only, or a full range.
 */
export function getLimitStatus(pct: number, thresholds: LimitThresholds = {}): LimitStatus {
  const { min, max, warningBuffer = 10 } = thresholds;

  if (typeof max === 'number' && pct > max) return 'exceeded';
  if (typeof min === 'number' && pct < min) return 'exceeded';
  if (typeof max === 'number' && pct >= max - warningBuffer) return 'near-limit';
  if (typeof min === 'number' && pct <= min + warningBuffer) return 'near-limit';
  return 'within-limit';
}
